/**
 * @file getOragizationQuery
 *
 * @fileoverview Gets the landing pages queries. The optional flags are checked as well.
 */
import { gql } from '@apollo/client';
import {
  FrontendConfigContextType,
  getCurrentFrontendConfig,
} from '~/jobiqo/context/frontendConfig';
import ORG_TEASER_LARGE_FRAGMENT from '~/jobiqo/components/Organization/Teaser/Large/graphql/fragments/organizationTeaserLargeFragment.js';
import { getServerFrontendConfig } from '~/jobiqo/lib/frontendConfig';
import BLOG_POST_FRAGMENT from '~/jobiqo/pages/blogs/graphql/fragments/blogPostFragment.gql';
import { isModuleEnabled } from '~/jobiqo/lib/config';

const getRuntimeFrontendConfig = (
  frontendConfig?: FrontendConfigContextType,
) => {
  if (frontendConfig) {
    return frontendConfig;
  }

  return typeof window === 'undefined'
    ? getServerFrontendConfig()
    : getCurrentFrontendConfig();
};

/**
 * Gets the query resolving a landing page by path.
 *
 * @param frontendConfig
 *   The frontend configuration, else the runtime one.
 * @param enabledModules
 *   The names of the backend modules the app config reports as enabled. Optional
 *   modules add paragraph types that only exist in the schema while they are
 *   enabled, and asking for an unknown type fails the whole query.
 *
 * @returns
 *   The landing page query.
 */
export const getLandingPageByPathQuery = (
  frontendConfig?: FrontendConfigContextType,
  enabledModules: string[] = [],
) => {
  return gql`
    ${getLandingPageFragment(frontendConfig, enabledModules)}
    query ROUTER_QUERY($path: String!, $language: String) {
      route(path: $path, language: $language) {
        ... on ContentResponse {
          content {
            ...LandingPageFragment
          }
        }
        ... on ErrorResponse {
          code
        }
        ... on RedirectResponse {
          code
          target
        }
      }
    }
  `;
};

/**
 * Gets the Job LP teaser fragment with the optional fields.
 */
export const getJobLpTeaserFragment = () => {
  const salaryRangeFreeInputEnabled = isModuleEnabled(
    'jobiqo_job_salary_range_free_input',
  );
  return gql`
    fragment JobTeaserLpFragment on Job {
      id
      title
      organization
      organizationProfile {
        name
        url {
          path
        }
        urlNoPrefix
        logo(style: "job_squared_logo_medium") {
          url
          alt
        }
        isPremium
      }
      url {
        path
      }
      urlNoPrefix
      logo(style: "job_squared_logo_small") {
        url
      }
      employmentType {
        tid
        label
      }
      occupationalField {
        tid
        label
      }
      address
      published
      expiration
      pause
      formattedOriginalPublished
      formattedPublished
      formattedExpiration
      formattedPause
      isFeatured
      isHighlight
      isTopJob
      bookmarked
      absoluteUrl
      absoluteOrgUrl
      videoUrl
      jobPostingFrontend
      salaryRange {
        tid
        label
      }
      ${
        salaryRangeFreeInputEnabled
          ? `
      salaryRangeFree {
        minSalary
        maxSalary
        currencyCode
        salaryUnit
      }
    `
          : ''
      }
    }
  `;
};

export const getLandingPageFragment = (
  frontendConfig?: FrontendConfigContextType,
  enabledModules: string[] = [],
) => {
  const LANDING_PAGE_AI_ENABLED = enabledModules.includes('jobiqo_landing_ai');
  const runtimeFrontendConfig = getRuntimeFrontendConfig(frontendConfig);
  const appConfig = runtimeFrontendConfig.APP || {};
  const jobsConfig = runtimeFrontendConfig.JOBS || {};
  const LANDING_PAGE_ADS_ACTIVE = appConfig.LANDING_PAGE_ADS_ACTIVE;
  const BLOG_FEATURE_ACTIVE = appConfig.BLOG_FEATURE_ACTIVE;
  const LOGO_CLOUD_SIMPLE_IMAGE_STYLE =
    appConfig.THEME?.LANDING_PAGES?.LOGO_CLOUD_SIMPLE?.IMAGE_STYLE || 'medium';
  const SHOW_ONLY_FEATURED_JOBS = jobsConfig.SHOW_ONLY_FEATURED_JOBS;
  const SHOW_ONLY_FEATURED_JOBS_BY_UPGRADE_FRONTEND =
    jobsConfig.SHOW_ONLY_FEATURED_JOBS_BY_UPGRADE_FRONTEND;
  const HIDE_BACKFILL_IMPORTER_JOBS =
    jobsConfig.FEATURED_JOBS_HIDE_BACKFILL_IMPORTER_JOBS;
  const FEATURED_JOBS_SETTINGS = jobsConfig.FEATURED_JOBS_SETTINGS || {};
  const SHOW_ONLY_FEATURED_VIDEO_JOBS =
    jobsConfig.SHOW_ONLY_FEATURED_VIDEO_JOBS;
  const VIDEO_JOBS_SETTINGS = jobsConfig.VIDEO_JOBS_SETTINGS || {};
  const SHOW_ONLY_TOP_JOBS = jobsConfig.SHOW_ONLY_TOP_JOBS;
  const HIDE_BACKFILL_IMPORTER_TOP_JOBS =
    jobsConfig.JOBS_OF_THE_WEEK_HIDE_BACKFILL_IMPORTER_JOBS;
  const TOP_JOBS_SETTINGS = jobsConfig.TOP_JOBS_SETTINGS || {};

  return gql`
    ${BLOG_FEATURE_ACTIVE ? BLOG_POST_FRAGMENT : ''}
    ${ORG_TEASER_LARGE_FRAGMENT}
    ${getJobLpTeaserFragment()}
    fragment LandingPageFragment on LandingPage {
      id
      title
      fixedHeader
      backgroundColor
      maxContainerSize
      landingPageRows {
        bundle
        ... on CallToActionBannerParagraph {
          bundle
          uuid
          callToActionImage(style: "banner_desktop") {
            url
            alt
            title
            width
            height
          }
          callToActionPortraitImage: callToActionImage(style: "banner_ipad") {
            url
            alt
            title
            width
            height
          }
          callToActionText
          spacing
          background
        }
        ... on SearchBannerParagraph {
          bundle
          uuid
          searchBannerImage(style: "banner_desktop") {
            url
            alt
            title
            width
            height
            caption
          }
          searchBannerPortraitImage: searchBannerImage(style: "banner_mobile") {
            url
            alt
            title
            width
            height
            caption
          }
          searchBannerTextLeft
          searchBannerTextRight
        }
        ... on SimpleTextParagraph {
          bundle
          uuid
          simpleText
          spacing
          background
          button {
            url
            text
          }
        }
        ${
          LANDING_PAGE_AI_ENABLED
            ? `... on AiGeneratedSiteParagraph {
          bundle
          uuid
          body
        }`
            : ''
        }
        ... on TextWithImageOnSideParagraph {
          bundle
          uuid
          featureElementDarkBg
          featureElementLink {
            url
            text
            linkStyle
          }
          featureElementImage(style: "large") {
            url
            alt
            title
            width
            height
            caption
          }
          featureElementDirection
          featureElementText
          spacing
          background
        }
        ... on MultiColImageTextParagraph {
          bundle
          uuid
          multiColItems {
            bundle
            ... on ColumnTextParagraph {
              columnText
            }
            ... on ImageTextParagraph {
              multiColImage(style: "large") {
                url
                alt
                title
                width
                height
                caption
              }
              multiColText
              button {
                url
                text
              }
            }
            ... on FacetBlockParagraph {
              title
              maxItems
              facet {
                name
                values {
                  label
                  filter
                  totalCount
                  alias
                }
              }
            }
            ${
              LANDING_PAGE_ADS_ACTIVE
                ? `
                ... on AdUnitColumnParagraph {
                  adUnitId
                }`
                : ''
            }

          }
          multiColIntroText
          multiColIntroTitle
          background
        }
        ...on FacetCategoriesParagraph {
          bundle
          uuid
          facetCategoriesTitle
          facetCategoriesSubTitle
          color
          spacing
          background
          text
          categories {
            title
            maxItems
            facet {
              name
              values {
                label
                filter
                totalCount
                alias
              }
            }
          }
        }
        ... on FeaturedJobsParagraph {
          bundle
          uuid
          introText
          introTextSecondary
          spacing
          background
          jobdata(
            featuredOnly: ${SHOW_ONLY_FEATURED_JOBS},
            ${
              SHOW_ONLY_FEATURED_JOBS_BY_UPGRADE_FRONTEND
                ? 'featuredByUpgradeFrontend: true,'
                : ''
            }
            hideBackfillImporterJobs: ${HIDE_BACKFILL_IMPORTER_JOBS},
            videoJobs: false
            ranges: ${
              FEATURED_JOBS_SETTINGS.RANGE
                ? `{ first: ${FEATURED_JOBS_SETTINGS.RANGE.FIRST}, last: ${FEATURED_JOBS_SETTINGS.RANGE.LAST} }`
                : null
            }
            ) {
            ... on SearchAPIJobResult {
              pages {
                 ...JobTeaserLpFragment
              }
            }
          }
        }
        ... on FeaturedVideoJobsParagraph {
          bundle
          introText
          introTextSecondary
          spacing
          background
          jobdata(
            featuredOnly: ${SHOW_ONLY_FEATURED_VIDEO_JOBS},
            hideBackfillImporterJobs: ${HIDE_BACKFILL_IMPORTER_JOBS},
            videoJobs: true,
            ranges: ${
              VIDEO_JOBS_SETTINGS.RANGE
                ? `{ first: ${VIDEO_JOBS_SETTINGS.RANGE.FIRST}, last: ${VIDEO_JOBS_SETTINGS.RANGE.LAST} }`
                : null
            }
            ) {
            ... on SearchAPIJobResult {
              pages {
                ...JobTeaserLpFragment
              }
            }
          }
        }
        ... on LpJobTeasersParagraph {
          bundle
          uuid
          jobTeasersTitle
          jobTeasersSubTitle
          background
          spacing
          url {
            path
          }
          jobs {
            ... on Job {
              id
              title
              organization
              organizationProfile {
                name
                url {
                  path
                }
                urlNoPrefix
                logo(style: "job_squared_logo_medium") {
                  url
                  alt
                  title
                }
                isPremium
              }
              url {
                path
              }
              urlNoPrefix
              logo(style: "job_squared_logo_small") {
                url
              }
              employmentType {
                tid
                label
              }
              occupationalField {
                tid
                label
              }
              address
              published
              expiration
              pause
              formattedOriginalPublished
              formattedPublished
              formattedExpiration
              formattedPause
              isFeatured
              isHighlight
              isTopJob
              bookmarked
            }
          }
        }
        ... on FeaturedOrganizationsParagraph {
          bundle
          uuid
          introText
          introTextSecondary
          spacing
          background
          industry {
            tid
          }
        }
        ... on HeroSimpleCenteredParagraph {
          bundle
          uuid
          overlayTransparency
          button1 {
            text
            url
          }
          button2 {
            text
            url
          }
          color
          h1
          image(style: "banner_desktop") {
            alt
            title
            url
            width
            height
            caption
          }
          subText
          spacing
          background
        }
        ... on FeatureCentered2ColumnsParagraph {
          bundle
          uuid
          featuredTitle
          featuredSubTitle
          text
          color
          items {
            bundle
            icon
            iconCustom(style: "large") {
              url
              alt
            }
            text
            title
          }
          spacing
          background
        }
        ... on StatsWithFadingBackgroundParagraph {
          bundle
          uuid
          statsSubtitle
          color
          image(style: "banner_desktop") {
            alt
            title
            url
            width
            height
          }
          items {
            bundle
            icon
            iconCustom(style: "large") {
              url
              alt
            }
            text
            title
          }
          text
          title
          spacing
        }
        ... on CtaSimpleParagraph {
          bundle
          uuid
          color
          button1 {
            text
            url
          }
          button2 {
            text
            url
          }
          subtitle
          title
          spacing
          background
        }
        ... on CtaSimpleBackgroundParagraph {
          bundle
          uuid
          color
          button1 {
            text
            url
          }
          button2 {
            text
            url
          }
          subtitle
          title
          spacing
          background
        }
        ... on HeroSearchBannerFullParagraph {
          bundle
          uuid
          overlayTransparency
          subText
          color
          h1
          image(style: "banner_desktop") {
            alt
            title
            url
            width
            height
            caption
          }
          button1 {
            text
            url
          }
          button2 {
            text
            url
          }
          spacing
        }
        ... on HeroVideoSearchBannerParagraph {
          bundle
          uuid
          overlayTransparency
          subText
          color
          h1
          videoUrl
          button1 {
            text
            url
          }
          button2 {
            text
            url
          }
          includeSearch
        }
        ... on HeroSearchBannerBackgroundParagraph {
          bundle
          uuid
          color
          h1
          rotatingWords
          links {
            text
            url
          }
          image(style: "banner_desktop") {
            alt
            title
            url
            width
            height
            caption
          }
          spacing
        }
        ... on FeatureGridWithOffsetIconsParagraph {
          bundle
          uuid
          uuid
          color
          featuredOffsetTitle
          featuredOffsetSubTitle
          text
          items {
            bundle
            icon
            iconCustom(style: "large") {
              url
              alt
            }
            text
            title
          }
          spacing
          background
        }
        ... on FeatureGridOnBrandParagraph {
          bundle
          uuid
          color
          items {
            bundle
            icon
            iconCustom(style: "large") {
              url
              alt
            }
            text
            title
          }
          featuredOnBrandSubtitle
          text
          title
          spacing
        }
        ... on HeroSplitWithScreenParagraph {
          bundle
          uuid
          h1
          bottomText
          bottomTextLink {
            text
            url
          }
          includeSearch
          color
          screenshotPosition
          screenshot(style: "banner_desktop") {
            alt
            title
            url
            width
            height
            caption
          }
          subText
          tagText
          text
          spacing
          background
        }
        ... on LogoCloudSimpleParagraph {
          bundle
          uuid
          text
          logos {
            link {
              text
              url
            }
            image(style: "${LOGO_CLOUD_SIMPLE_IMAGE_STYLE}") {
              url
              alt
              title
              width
              height
            }
          }
          spacing
          background
          display
          sliderRows
        }
        ... on EmbeddedYoutubeParagraph {
          bundle
          uuid
          embeddedYoutubeTitle: title
          width
          height
          link {
            text
            url
          }
          spacing
          background
        }
        ... on SimpleSearchBarParagraph {
          bundle
          uuid
          spacing
          background
        }
        ... on RssFeedParagraph {
          bundle
          uuid
          rssFeedTitle
          rssFeedSubTitle
          color
          spacing
          feedsItems: items {
            title
            description
            link {
              url
              text
            }
            image {
              url
              text
            }
          }
        }
        ... on ProductsGridParagraph {
          bundle
          productsSubtitle
          productsTitle
          text
          spacing
          backgroundColor
          gridDisplayOptions
          color
          items {
            ... on CtaProductParagraph {
              bundle
              intro
              content
              title
              titleIntroAlignment
              priceNumber
              priceCurrency
              price {
                number
                currency_code
              }
              button {
                url
                text
              }
              buttonAlignment
              image(style: "product_card_banner") {
                url
                alt
                title
                width
                height
              }
            }
            ... on ProductParagraph {
              bundle
              uuid
              product {
                id
                name
                description
                priceNumber
                priceCurrency
                priceUrl
                icon
              }
            }
          }
        }
        ... on InsecureScriptParagraph {
          bundle
          uuid
          scriptContent
        }
        ... on CobrandedGatewayParagraph {
          bundle
          uuid
          content: text
          logo(style: "large") {
            url
            alt
            title
            width
            height
          }
        }
        ... on VideoTeasersParagraph {
          bundle
          uuid
          teasersRowTitle
          items {
            ... on VideoTeaserItemParagraph {
              bundle
              teaserTitle
              logo(style: "large") {
                url
                alt
              }
              link {
                url
                text
              }
              subtitle
            }
          }
        }
        ... on TopJobsParagraph {
          bundle
          uuid
          introText
          introTextSecondary
          spacing
          background
          jobdata(
            topJobsOnly: ${SHOW_ONLY_TOP_JOBS},
            hideBackfillImporterJobs: ${HIDE_BACKFILL_IMPORTER_TOP_JOBS},
            ranges: ${
              TOP_JOBS_SETTINGS.RANGE
                ? `{ first: ${TOP_JOBS_SETTINGS.RANGE.FIRST}, last: ${TOP_JOBS_SETTINGS.RANGE.LAST} }`
                : null
            }
            ) {
            ... on SearchAPIJobResult {
              pages {
                 ...JobTeaserLpFragment
              }
            }
          }
        }
        ... on CtaButtonParagraph {
          bundle
          uuid
          color
          button1 {
            text
            url
          }
          spacing
          background
        }
        ... on CardsSliderParagraph {
          bundle
          uuid
          backgroundColor
          cardsSliderTitle
          spacing
          text
          items {
            ... on LpQuoteParagraph {
              bundle
              text
              author
              image(style: "medium") {
                url
                alt
                title
                width
                height
              }
            }
            ... on HorizontalCardParagraph {
              bundle
              title
              horizontalCardContent
              horizontalCardImage(style: "large") {
                url
                alt
                title
                width
                height
              }
              videoLink {
                url
                text
              }
              button {
                url
                text
              }
            }
          }
        }
        ... on HorizontalCardParagraph {
          bundle
          uuid
          title
          horizontalCardContent
          horizontalCardImage(style: "large") {
            url
            alt
            title
            width
            height
          }
          videoLink {
            url
            text
          }
          button {
            url
            text
          }
        }
        ... on TrainingOrganizationsParagraph {
          bundle
          uuid
          introText
          introTextSecondary
          spacing
          background
          sortField
          sortOrder
          sliderRows
        }
        ... on HeroSearchBannerCenteredParagraph {
          bundle
          uuid
          text
          image(style: "banner_desktop") {
            alt
            title
            url
            width
            height
            caption
          }
          spacing
          overlayTransparency
        }
        ... on TextWithMultipleButtonsParagraph {
          bundle
          uuid
          text
          links {
            text
            url
          }
          spacing
          background
          color
        }
        ... on RegionalJobsParagraph {
          bundle
          uuid
          regionalJobsTitle
          regionalJobsSubTitle
          location
          lat
          lon
          radius
          maxItems
          spacing
          background
          jobdata {
            ... on SearchAPIJobResult {
              pages {
                ...JobTeaserLpFragment
              }
            }
          }
        }
         ... on OrganizationProfileParagraph {
            ...OrganizationTeaserLargeFragment
          }
        ${
          LANDING_PAGE_ADS_ACTIVE
            ? `
              ... on RowAdUnitParagraph {
                bundle
                adUnitId
              }`
            : ''
        }
        ${
          BLOG_FEATURE_ACTIVE
            ? `
          ... on FeaturedBlogsParagraph {
            bundle
            uuid
            introText
            introTextSecondary
            spacing
            background
            blogs {
              items {
                ... on BlogPost {
                  ...BlogPostFragment
                }
              }
            }
          }
          ... on BlogTeasersParagraph {
            bundle
            uuid
            introText
            introTextSecondary
            posts {
              id
              title
              category {
                label
                tid
              }
              url {
                path
              }
              summary
              featured
              image(style: "blog_teaser_cover") {
                url
                width
                height
                alt
                title
              }
            }
          }
        `
            : ''
        }
      }
    }
  `;
};
